import React from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { blockComponents, inlineComponents } from "./markdownComponents";

type MainStructureNewsProps = {
    heading_1: string; subtitle_1: string; quote_1: string; quote_author_1: string; image_1: string; image_caption_1: string;
    heading_2: string; subtitle_2: string; quote_2: string; quote_author_2: string; image_2: string; image_caption_2: string;
    heading_3: string; subtitle_3: string; quote_3: string; quote_author_3: string; image_3: string; image_caption_3: string;
    heading_4: string; subtitle_4: string; quote_4: string; quote_author_4: string; image_4: string; image_caption_4: string;
    heading_5: string; subtitle_5: string; quote_5: string; quote_author_5: string; image_5: string; image_caption_5: string;
};

const GradientLine = () => (
    <div className="h-px w-full bg-gradient-to-r from-gradient-start to-gradient-end my-12 opacity-20" />
);

/* -------------------------------------------------------------------------- */
/*  Markdown helpers                                                          */
/* -------------------------------------------------------------------------- */

// I renderer (link + liste puntate/numerate) vivono in ./markdownComponents,
// così MainStructureNews e HeadingAndSubtitle si comportano allo stesso modo.

/* -------------------------------------------------------------------------- */
/*  Sub-blocks                                                                */
/* -------------------------------------------------------------------------- */

const QuoteBlock = ({ quote, author }: { quote: string; author?: string }) => (
    <div
        className="border-l-2 pl-8 my-10"
        style={{ borderImage: "linear-gradient(180deg, #00858F 0%, #E6007E 100%) 1" }}
    >
        <div className="text-white text-xl md:text-2xl italic leading-relaxed opacity-90">
            <ReactMarkdown remarkPlugins={[remarkBreaks]} components={blockComponents}>
                {quote?.replace(/\\n/g, "\n")}
            </ReactMarkdown>
        </div>
        {author && (
            <span
                className="text-xs uppercase tracking-widest font-bold mt-3 block"
                style={{
                    background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                }}
            >
                {author?.replace(/\\n/g, " ")}
            </span>
        )}
    </div>
);

const ImageBlock = ({ src, caption }: { src: string; caption?: string }) => (
    <div className="my-10">
        <img
            className="w-full object-contain mx-auto"
            src={src}
            alt={caption || ""}
        />
        {caption && (
            // <div> e non <p>: se la didascalia contiene un elenco, un <ul>
            // dentro un <p> farebbe chiudere il paragrafo al browser.
            <div
                className="text-center text-xs uppercase tracking-widest mt-4 font-bold"
                style={{
                    background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                }}
            >
                <ReactMarkdown remarkPlugins={[remarkBreaks]} components={inlineComponents}>
                    {caption?.replace(/\\n/g, "\n")}
                </ReactMarkdown>
            </div>
        )}
    </div>
);

type SectionProps = {
    heading?: string;
    subtitle?: string;
    quote?: string;
    quote_author?: string;
    image?: string;
    image_caption?: string;
    showDivider?: boolean;
};

const Section = ({ heading, subtitle, quote, quote_author, image, image_caption, showDivider }: SectionProps) => {
    if (!heading && !subtitle && !quote && !image) return null;
    return (
        <>
            {showDivider && <GradientLine />}
            <div className="space-y-6">
                {heading && (
                    // Heading: ReactMarkdown dentro <h2> con inlineComponents
                    // così non viene generato un <p> annidato (HTML invalido).
                    <h2 className="text-white font-bold text-xl md:text-2xl uppercase tracking-tight leading-snug">
                        <ReactMarkdown remarkPlugins={[remarkBreaks]} components={inlineComponents}>
                            {heading?.replace(/\\n/g, "\n")}
                        </ReactMarkdown>
                    </h2>
                )}
                {subtitle && (
                    <div className="text-white text-opacity-80 text-base md:text-lg leading-loose">
                        <ReactMarkdown remarkPlugins={[remarkBreaks]} components={blockComponents}>
                            {subtitle?.replace(/\\n/g, "\n")}
                        </ReactMarkdown>
                    </div>
                )}
                {quote && <QuoteBlock quote={quote} author={quote_author} />}
                {image && <ImageBlock src={image} caption={image_caption} />}
            </div>
        </>
    );
};

const MainStructureNews: React.FC<MainStructureNewsProps> = (props) => {
    const sections = [
        { heading: props.heading_1, subtitle: props.subtitle_1, quote: props.quote_1, quote_author: props.quote_author_1, image: props.image_1, image_caption: props.image_caption_1 },
        { heading: props.heading_2, subtitle: props.subtitle_2, quote: props.quote_2, quote_author: props.quote_author_2, image: props.image_2, image_caption: props.image_caption_2 },
        { heading: props.heading_3, subtitle: props.subtitle_3, quote: props.quote_3, quote_author: props.quote_author_3, image: props.image_3, image_caption: props.image_caption_3 },
        { heading: props.heading_4, subtitle: props.subtitle_4, quote: props.quote_4, quote_author: props.quote_author_4, image: props.image_4, image_caption: props.image_caption_4 },
        { heading: props.heading_5, subtitle: props.subtitle_5, quote: props.quote_5, quote_author: props.quote_author_5, image: props.image_5, image_caption: props.image_caption_5 },
    ];

    return (
        <div>
            {sections.map((section, i) => (
                <Section key={i} {...section} showDivider={i > 0} />
            ))}
        </div>
    );
};

export default MainStructureNews;
